// TODO: This file must be broken appart and it's component
// moved to the correct lib

import type { CreatorBySlugQueryResult } from '../../components/CreatorPage/CreatorPage.types';
import type {
  CMSMarketplaceTrip,
  ContentByIdMap,
  SanityUUID,
} from '../../data/sanity/types';
import type {
  HotelIngredientResponseQl,
  IngredientQl,
  IngredientResponseQl,
  ItineraryResponseQl,
  TicketIngredientResponseQl,
} from '../../data/smart-search/hooks';
import { ContentApiService } from '../../services/ContentApiService/ContentApiService';
import type {
  HotelSearchItem,
  PlaceInformation,
  ScoresMap,
} from '../../services/ContentApiService/ContentApiService.types';
import { ContentService } from '../../services/ContentService/ContentService';
import {
  CONTENT_API_BASE_URL,
  IMAGE_SERVICE_BASE_URL,
} from '../../utils/constants';
import { logs } from '../../utils/logs';
import type { SemboMarket } from '../../utils/markets';
import { getHotelSearchPolygonId } from './helpers';
import {
  type AdyenPaymentMethodsResponse,
  type Airport,
  type PaymentMethod,
  type PlacesLists,
  type ProductData,
  type ProductType,
  ResizeMode,
  type SemboImageOptions,
} from './types';

export const getProductType = (
  code: string,
  response: ItineraryResponseQl
): ProductType => {
  return response.result?.find((r) =>
    (r as HotelIngredientResponseQl).subIngredientResults?.find(
      (si) => (si as TicketIngredientResponseQl).code === code
    )
  )
    ? 'Ticket'
    : 'Hotel';
};

export const getPlaceByPolygonId = async (
  market: SemboMarket,
  polygonId: number
): Promise<PlaceInformation | null> => {
  try {
    const data = await ContentApiService.getPlaceByPolygonId(
      polygonId.toString(),
      market
    );

    if (!data) {
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

export const getPolygonIdFromHotel = async (
  term: string,
  market: string
): Promise<number | null | undefined> => {
  const result = await ContentService.getHotel({
    searchText: { searchText: term },
    market,
    searchOptions: { useHistoricalCodes: true },
    includeFields: [
      'Hotels.MainPolygon.Id',
      'Polygons.PolygonId',
      'Polygons.Type',
    ],
  });

  return getHotelSearchPolygonId(
    result?.Polygons,
    result?.Hotels?.[0]?.MainPolygon?.Id
  );
};

export type PlaceRedirectInformation = {
  NewUrlSlug: string;
};

export const getPlace = async (
  market: SemboMarket,
  placeSlug: string
): Promise<PlaceInformation | PlaceRedirectInformation> => {
  const url = `${CONTENT_API_BASE_URL}/Place/` + placeSlug;

  return (
    await fetch(url, {
      method: 'GET',
      headers: { 'X-Sembo-Host': market.host, 'X-Sembo-App': 'Inspire' },
    })
  ).json();
};

export const getSemboImageUrl = (
  imageName: string,
  imageService = 'sembo',
  options?: SemboImageOptions
): string => {
  const {
    width,
    height,
    resizeMode,
    timeoutSeconds,
    hotelCode,
    shrinkRectangle,
  } = options || {};

  const url = new URL(IMAGE_SERVICE_BASE_URL);

  // IMPORTANT: parameters are set in correct order (do not change)
  url.searchParams.set('service', imageService);
  url.searchParams.set('nameOfImage', imageName);

  if (resizeMode !== undefined) {
    url.searchParams.set('resizeMode', resizeMode);
  }

  if (width !== undefined) {
    url.searchParams.set('width', width.toString());
  }

  if (height !== undefined) {
    url.searchParams.set('height', height.toString());
  }

  if (timeoutSeconds !== undefined) {
    url.searchParams.set('timeoutSeconds', timeoutSeconds.toString());
  }

  if (hotelCode !== undefined) {
    url.searchParams.set('hotelCode', hotelCode);
  }

  if (shrinkRectangle !== undefined) {
    url.searchParams.set('shrinkRectangle', shrinkRectangle.toString());
  }

  url.searchParams.set('semboApp', 'true');

  return url.toString();
};

export const isSemboTravelImageCdn = (url: string) => {
  if (url.startsWith('/m/')) {
    return false;
  }

  if (url.includes('images.sembo.travel')) {
    return true;
  }

  try {
    new URL(url);
    return false;
  } catch (e) {
    // our cdn usually returns just a filename
    // so the try/catch will fail
    return true;
  }
};

export const getImageURL = (
  imageName: string,
  imageService?: string,
  width?: number,
  height?: number
): string => {
  if (!isSemboTravelImageCdn(imageName) && !imageService) {
    const url = new URL(imageName);
    // most image sources support https, but we store them as http
    url.protocol = 'https:';

    return url.toString();
  }

  if (imageName.includes(IMAGE_SERVICE_BASE_URL)) {
    return imageName;
  }

  const determinedHeight = height ?? 9999;

  const options: SemboImageOptions = {
    resizeMode: height ? ResizeMode.FitOutside : ResizeMode.FitInside,
  };

  if (width !== undefined) {
    options.width = width;
    options.height = determinedHeight;
  }

  return getSemboImageUrl(imageName, imageService, options);
};

const id = <T>(x: T) => x;

const extractCode = (
  subIngredient: IngredientResponseQl | IngredientQl
): string => (subIngredient as IngredientQl).code ?? '';

function getCodesFromResult(
  r: HotelIngredientResponseQl,
  onlySpecifiedHotelCodes?: string[]
): string[] {
  const xs = [];
  if ('suggested' in r && r.suggested?.hotelCode) {
    if (
      !onlySpecifiedHotelCodes ||
      onlySpecifiedHotelCodes.includes(r.suggested.hotelCode)
    )
      xs.push(r.suggested.hotelCode);
  }
  if ('subIngredientResults' in r) {
    xs.push(
      (r.subIngredientResults as IngredientResponseQl[])
        ?.flatMap(extractCode)
        .filter(id)
    );
  }
  return xs.flat() as string[];
}

export function getCodes(
  itinerary: ItineraryResponseQl,
  onlySpecifiedHotelCodes?: string[]
): string[] {
  return (itinerary.result as IngredientResponseQl[]).flatMap((result) =>
    getCodesFromResult(result, onlySpecifiedHotelCodes)
  );
}

export async function getProductData(
  suggestion: ItineraryResponseQl,
  market: SemboMarket
): Promise<ProductData[]> {
  const codes = getCodes(suggestion);
  if (!codes || codes.length === 0) return [];

  const promises = codes
    .flatMap((code) => {
      const flattenedCodes = Array.isArray(code) ? code : [code];

      return flattenedCodes.map(async (code) => {
        const hotelData = await ContentService.getHotel({
          searchText: { hotelCode: code },
          market: market.host,
          searchOptions: { perfectMatchOnly: true },
          includeFields: [
            'Hotels.HotelCode',
            'Hotels.Name',
            'Hotels.MainPolygon.FullName',
            'Hotels.Images.ImageName',
            'Hotels.Images.ImageService',
            'Hotels.SemboSunRating',
          ],
        });

        const { Name, Images, SemboSunRating, HotelCode, MainPolygon } =
          (hotelData.Hotels && hotelData.Hotels[0]) || {};
        const imgRef = Images && Images[0]?.ImageName;
        const imageURL = imgRef
          ? getImageURL(imgRef, Images[0]?.ImageService)
          : null;

        if (!imgRef) {
          logs.warning({
            message: 'Images[0]?.ImageName is returning undefined',
            url: code,
          });
        }

        const type = getProductType(HotelCode, suggestion);
        return {
          code: HotelCode,
          type,
          name: Name,
          imageURL,
          rating: SemboSunRating && Number(SemboSunRating),
          city: MainPolygon?.FullName.split(',')[0].trim(),
          country: MainPolygon?.FullName.split(',').slice(1).join().trim(),
        };
      });
    })
    .flat();
  try {
    const result = await Promise.all(promises);
    return result;
  } catch (_e) {
    logs.error(
      {
        message: 'Failed to fetch product data',
        product: {
          suggestion,
          market,
        },
        error: _e,
      },
      'PRODUCT_DATA_FETCH_ERROR'
    );
    return [];
  }
}

export const getHarbourByIata = async (iata: string, market: SemboMarket) => {
  try {
    const data = await ContentApiService.getHarbourByIata(iata, market);
    const harbour = data?.[0]?.harbour;

    if (!harbour) {
      return { name: '', city: '' };
    }

    return {
      name: harbour.address ?? '',
      city: harbour.label ?? '',
    };
  } catch {
    return { name: '', city: '' };
  }
};

export const getAirportIataByNearestDestination = async (
  searchText: string,
  market: SemboMarket
): Promise<string | null> => {
  try {
    const data = await ContentApiService.getAirportForDestination(
      searchText,
      market
    );

    return data[0]?.airport?.code ?? null;
  } catch {
    return null;
  }
};

export const getAirportByIata = async (
  iata: string,
  market: SemboMarket
): Promise<Airport | null> => {
  try {
    const data = await ContentApiService.getAirportByIata(iata, market);
    const airport = data[0]?.airport;

    if (!airport) {
      return null;
    }

    return {
      name: airport.label,
      city: airport.city,
      polygonId: airport.polygonId,
    };
  } catch {
    return null;
  }
};

export const getHotelByCode = async (
  code: string,
  market: SemboMarket
): Promise<HotelSearchItem | null> => {
  try {
    const result = await ContentApiService.searchHotels(
      {
        searchText: `hotel-code:${code}`,
        maxNumberOfHotels: '1',
        perfectMatchOnly: 'true',
        include: [
          'Hotels.HotelCode',
          'Hotels.Name',
          'Hotels.MainPolygon.FullName',
          'Hotels.SemboSunRating',
        ],
      },
      market
    );

    return result.Hotels?.[0] ?? null;
  } catch {
    return null;
  }
};

export const addPopularityScoreToTrips = async (
  market: SemboMarket,
  trips: CMSMarketplaceTrip[]
): Promise<CMSMarketplaceTrip[]> => {
  const polygonIds: Set<number> = new Set();

  // collect the polygon ids
  trips.forEach(
    (trip) =>
      trip?.recipe?.ingredients
        ?.filter((ingredient) =>
          ingredient?.destination?.startsWith('polygon-id')
        )
        ?.forEach((ingredient) => {
          const polygonId = ingredient?.destination?.replace('polygon-id:', '');
          if (polygonId) {
            polygonIds.add(parseInt(polygonId));
          }
        }) ?? []
  );

  const scoresMap = await getPolygonsScore(market, Array.from(polygonIds));

  // update the highestIngredientScore for each trip
  const updatedTrips = trips.map((trip) => {
    const ingredientScores =
      trip?.recipe?.ingredients
        ?.filter((ingredient) =>
          ingredient?.destination?.startsWith('polygon-id')
        )
        ?.map((ingredient) => {
          const polygonId = ingredient?.destination?.replace('polygon-id:', '');
          if (polygonId) {
            const polygonResult = scoresMap?.[polygonId];

            return polygonResult?.AverageScore ?? 0;
          }
          return 0;
        }) ?? [];

    return {
      ...trip,
      highestIngredientScore: Math.max(0, ...ingredientScores),
    };
  });

  return updatedTrips;
};

export const getPolygonsScore = async (
  market: SemboMarket,
  polygonIds: number[]
): Promise<ScoresMap> => {
  try {
    const data = await ContentApiService.getPolygonsScore(polygonIds, market);

    if (!data) {
      return {};
    }

    return data.PolygonScores ?? {};
  } catch {
    return {};
  }
};

export const sortTripIdsByHighestIngredientScore = (
  tripIds: SanityUUID[],
  tripsMap: ContentByIdMap<CMSMarketplaceTrip>,
  prioritizedHighlights: string[] = [],
  direction: 'asc' | 'desc' = 'desc'
): SanityUUID[] =>
  tripIds.sort((a, b) => {
    const tripA = tripsMap[a];
    const tripB = tripsMap[b];

    const scoreA = tripA?.highestIngredientScore ?? 0;
    const scoreB = tripB?.highestIngredientScore ?? 0;

    const hasPriorityA =
      tripA?.highlight && prioritizedHighlights.includes(tripA?.highlight);
    const hasPriorityB =
      tripB?.highlight && prioritizedHighlights.includes(tripB?.highlight);

    if (hasPriorityA && !hasPriorityB) {
      return -1;
    }

    if (!hasPriorityA && hasPriorityB) {
      return 1;
    }

    // If both have prioritized labels or neither has, sort by score
    if (direction === 'asc') {
      return scoreA - scoreB;
    }
    return scoreB - scoreA;
  });

export const sortTripIdsByDepartureDate = (
  tripIds: SanityUUID[],
  tripsMap: ContentByIdMap<CMSMarketplaceTrip>
): SanityUUID[] =>
  [...tripIds].sort((a, b) => {
    const dateA =
      tripsMap[a]?.recipePreferences?.mode === 'specificDates'
        ? (tripsMap[a].recipePreferences.specificDates?.firstArrivalDate ?? '')
        : '';
    const dateB =
      tripsMap[b]?.recipePreferences?.mode === 'specificDates'
        ? (tripsMap[b].recipePreferences.specificDates?.firstArrivalDate ?? '')
        : '';
    return dateA.localeCompare(dateB);
  });

export const sortTripIds = (
  tripIds: SanityUUID[],
  tripsMap: ContentByIdMap<CMSMarketplaceTrip>
): SanityUUID[] => {
  const dated = tripIds.filter(
    (id) => tripsMap[id]?.recipePreferences?.mode === 'specificDates'
  );
  const undated = tripIds.filter(
    (id) => tripsMap[id]?.recipePreferences?.mode !== 'specificDates'
  );

  return [
    ...sortTripIdsByDepartureDate(dated, tripsMap),
    ...sortTripIdsByHighestIngredientScore(undated, tripsMap),
  ];
};

export const withRetries = async <Result>(
  callback: () => Promise<Result>,
  retryTimeOuts = [0, 50, 100, 200, 500, 1000]
): Promise<Result> => {
  let ex: unknown;

  for (const timeout of retryTimeOuts) {
    try {
      const result = await callback();
      return result;
    } catch (e) {
      ex = e;
    }

    await delay(timeout);
  }

  throw ex;
};

export const delay = (timeout: number) =>
  new Promise((resolve) => setTimeout(resolve, timeout));

export const getPlacesLists = async (
  market: SemboMarket,
  page?: number,
  limit?: number
): Promise<PlacesLists> => {
  const [countriesSortedByScore, citiesSortedByScore] = await Promise.all([
    ContentApiService.getPlaces(
      {
        type: 'Country',
        sort: 'score',
        page: page?.toString(),
        size: limit?.toString(),
      },
      market
    ),
    ContentApiService.getPlaces(
      {
        type: 'City',
        sort: 'score',
        page: page?.toString(),
        size: limit?.toString(),
      },
      market
    ),
  ]);

  return [
    {
      translationKey: 'explore.popularCountries',
      places: countriesSortedByScore,
    },
    {
      translationKey: 'explore.popularCities',
      places: citiesSortedByScore,
    },
  ].map((item) => ({
    ...item,
    links: item.places.map((place) => ({
      url: `/places/${place.UrlSlug}`,
      name: place.PolygonName,
    })),
  }));
};

export const doesCreatorContainTrip = (
  creator: CreatorBySlugQueryResult,
  tripId: SanityUUID
): boolean => {
  if (!creator || !creator.tripsCollections) {
    return false;
  }

  for (const tripCollection of creator.tripsCollections) {
    if (tripCollection.tripIds.includes(tripId)) {
      return true;
    }
  }

  return false;
};

const fetchPaymentMethods = async (
  market: SemboMarket,
  origin: string
): Promise<AdyenPaymentMethodsResponse> => {
  return (
    await fetch(
      `https://${market.host}/d/mvc/Adyen/GetPaymentMethodsForMarket?origin=${origin}`
    )
  ).json();
};

export const getPaymentMethods = async (
  market: SemboMarket,
  origin = 'inspire'
): Promise<PaymentMethod[]> => {
  try {
    const res = await fetchPaymentMethods(market, origin);

    const getLogoUrl = (name: string): string =>
      `https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/${name}.svg`;

    const paymentMethods = res.paymentMethods.reduce(
      (methods, { name, type, brands }) => {
        if (type === 'scheme' && brands) {
          methods.push(
            ...brands.map((brand) => ({
              name: brand,
              type: brand,
              logoUrl: getLogoUrl(brand),
            }))
          );
        } else {
          methods.push({
            name,
            type,
            logoUrl: getLogoUrl(type),
          });
        }

        return methods;
      },
      [] as PaymentMethod[]
    );

    return paymentMethods;
  } catch (e) {
    return [];
  }
};
